import React, { useState, useEffect, useRef } from 'react';
import { useUsers, usePreGame, useActiveGame, useAppConfig } from '@/hooks/useShared';
import { startGame, updatePlayerRSVP, addPlayer } from '@/hooks/useGameSetup';
import { useAuth } from '@/hooks/useAuth';
import { canManageUsers } from '@/lib/roles';
import { AddPlayerModal } from '@/components/setup/AddPlayerModal';
import { LoadingSpinner, EmptyState } from '@/components/ui';
import { toast } from 'sonner';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useNavigate } from 'react-router-dom';

function GoogleLoginFAB({ onSignIn }: { onSignIn: () => Promise<void> }) {
  const navigate = useNavigate();

  const handleGoogleLogin = async () => {
    try {
      await onSignIn();
      navigate('/profile');
    } catch (err: any) {
      if (err?.code === 'auth/popup-blocked') {
        toast.error('Popup blocked — please allow popups for this site');
      } else if (err?.code === 'auth/unauthorized-domain') {
        toast.error('This domain is not authorized for Google sign-in');
      } else {
        toast.error('Sign-in failed. Please try again.');
      }
    }
  };

  return (
    <button
      onClick={handleGoogleLogin}
      className="fixed bottom-6 right-6 w-14 h-14 bg-blue-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-700 transition-colors z-50"
      aria-label="Sign in with Google"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
      </svg>
    </button>
  );
}

export function SetupScreen() {
  const { preGame, loading: preGameLoading } = usePreGame();
  const { activeGame, loading: activeGameLoading } = useActiveGame();
  useAppConfig();
  const game = preGame;
  const { users, loading: usersLoading } = useUsers();
  const { user, firebaseUser, signIn } = useAuth();
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  
  const prevPreGameRef = useRef(preGame);
  const redirectPendingRef = useRef(false);
  const isActiveGameRunning = activeGame?.status === 'active' || activeGame?.status === 'chip_entry';

  useEffect(() => {
    if (prevPreGameRef.current && !preGame && activeGame?.status === 'active' && !redirectPendingRef.current) {
      redirectPendingRef.current = true;
      toast.success('The game has started! Redirecting to live board...');
      setTimeout(() => {
        navigate('/game');
      }, 3000);
    }
    prevPreGameRef.current = preGame;
  }, [preGame, activeGame, navigate]);

  const isAdmin = canManageUsers(user);
  
  if (preGameLoading || activeGameLoading || usersLoading) {
    return (
      <div className="min-h-screen bg-[#f5f7f8] flex items-center justify-center">
        <LoadingSpinner message="Loading Session..." />
      </div>
    );
  }
  
  if (!game) {
    return (
      <div className="pb-[180px]">
        <div className="max-w-[400px] mx-auto">
          <EmptyState icon="🃏" title="No Scheduled Game" description="No upcoming poker game found." />
          <button
            onClick={() => navigate('/setup/new')}
            disabled={isActiveGameRunning}
            className={`w-full bg-[#006855] text-white border-none rounded-full py-4 px-6 cursor-pointer flex items-center justify-center transition-opacity ${isActiveGameRunning ? 'opacity-50' : 'opacity-100'}`}
          >
            <span className="text-sm font-bold">
              {isActiveGameRunning ? 'Cannot schedule while game active' : 'New Game'}
            </span>
          </button>
        </div>
      </div>
    );
  }

  const isReadOnly = game.status !== 'setup';
  const confirmedCount = game.players.filter(p => p.rsvp_status === 'yes' || p.rsvp_unified === 'confirmed').length;
  const canStart = confirmedCount >= 2;

  const date = new Date(game.date);
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const handleStart = async () => {
    if (!game || !isAdmin) return;

    setStarting(true);
    try {
      await startGame(game.id, user?.id ?? '');
      navigate('/game');
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setStarting(false);
    }
  };

  const handleRSVP = async (status: 'yes' | 'maybe' | 'no', playerIdx?: number) => {
    if (!game || game.status !== 'setup') return;
    const idx = playerIdx !== undefined 
      ? playerIdx 
      : (user ? game.players.findIndex(p => p.user_id === user.id) : -1);
    if (idx === -1) return;
    const player = game.players[idx];
    if (!isAdmin && player.user_id !== user?.id) return;
    await updatePlayerRSVP(game.id, idx, status);
  };

  const handleRemovePlayer = async (userId: string) => {
    if (!game || !confirm('Remove this player?')) return;
    const newPlayers = game.players.filter(p => p.user_id !== userId);
    await updateDoc(doc(db, 'games', game.id), { players: newPlayers });
  };

  const handleAddPlayer = async (playerData: { user_id: string; name: string; email?: string }) => {
    if (!game) return;
    await addPlayer(game.id, playerData);
  };

  return (
    <div className="pb-[180px]">
      <div className="max-w-[400px] mx-auto">
        {isActiveGameRunning && (
          <div className="bg-[#fffbe6] border border-[#ffe58f] rounded-xl p-3 mb-4 flex items-center justify-between shadow-sm">
            <div className="text-sm font-semibold text-[#856404]">A game is currently in progress!</div>
            <button onClick={() => navigate('/game')} className="bg-[#006855] text-white border-none rounded-lg px-3 py-1.5 text-xs font-bold cursor-pointer">
              Go to Game
            </button>
          </div>
        )}
        
        {/* Hero Card */}
        <div className="bg-[#e8f5f0] border border-aura-primary/10 shadow-sm rounded-2xl p-5 mb-4 relative">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#006855] opacity-70 mb-1">Upcoming Session</div>
          <div className="text-[22px] font-extrabold text-[#2c2f30] mb-3 leading-[1.2] flex items-baseline flex-wrap">
            {game.venue || 'Friday Night Poker'}
            {game.venue_address && <span className="text-[13px] text-text-muted opacity-80 ml-2 font-normal">{game.venue_address}</span>}
          </div>
            <div className="flex justify-between items-center">
              <div className="text-[13px] text-[#006855] font-bold opacity-80">{dateStr} · {game.time || '20:30'}</div>
              <div className="text-[13px] text-[#006855] font-black">{confirmedCount} Confirmed</div>
            </div>
          </div>
        
          <div className="flex gap-3 mb-4">
            {isAdmin && game.status === 'setup' && (
              <>
                <button 
                  onClick={() => navigate(`/setup/edit/${game.id}`)}
                  className="flex-1 py-2 px-3 rounded-xl bg-white border border-[#006855] text-[#006855] text-xs font-bold cursor-pointer hover:bg-aura-primary/5 transition-colors shadow-sm"
                >
                  Edit Session
                </button>
                <button 
                  onClick={() => setShowAddPlayerModal(true)} 
                  className="flex-1 py-2 px-3 rounded-xl bg-[#006855] text-white text-xs font-bold cursor-pointer shadow-sm hover:bg-[#005b4a] transition-colors"
                >
                  Add Player
                </button>
              </>
            )}
          </div>


         {isAdmin && !game && activeGame?.status === 'settling' && (
           <div 
             onClick={() => navigate('/setup/new')}
             className="bg-white border border-[#006855] rounded-xl p-4 mb-4 cursor-pointer text-center shadow-sm shadow-[#006855]/10"
           >
             <div className="text-sm font-bold text-[#006855]">Schedule Next Session</div>
             <div className="text-xs text-[#595c5d]">The current game is settling. Plan the next one!</div>
           </div>
         )}

         {/* Player List */}
         <div className="mb-8">
           <div className="flex items-center justify-between mb-3 px-1">
             <div className="text-xs font-bold uppercase tracking-wider text-[#595c5d]">Players</div>
             <div className="text-[10px] font-bold text-[#006855]">{game.players.length} Total</div>
           </div>
           <div className="space-y-2">
             {game.players.length === 0 ? (
               <div className="text-center py-6 bg-white border border-dashed border-[#ddd] rounded-xl text-xs text-[#888]">
                 No players added yet
               </div>
             ) : (
                game.players.map((player, index) => (
                  <div 
                    key={player.user_id} 
                    className="flex items-center justify-between p-3 bg-white border border-[#eee] rounded-xl shadow-sm"
                  >
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-[#e8f5f0] text-[#006855] flex items-center justify-center text-[10px] font-bold">
                          {player.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="text-sm font-semibold text-[#2c2f30]">{player.name}</div>
                      </div>
                      <div className="flex gap-1 ml-10">
                        {(['yes', 'maybe', 'no'] as const).map((status) => (
                          <button
                            key={status}
                            onClick={() => handleRSVP(status, index)}
                            disabled={!isAdmin && player.user_id !== user?.id}
                             className={`rounded-full px-2 py-0.5 text-[10px] font-bold transition-colors ${
                               (player.rsvp_status === status || player.rsvp_unified === (status === 'yes' ? 'confirmed' : status === 'no' ? 'declined' : 'maybe'))
                                 ? 'bg-[#006855] text-white' 
                                 : 'bg-gray-100 text-gray-500'
                             } ${(!isAdmin && player.user_id !== user?.id) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {isAdmin && (
                        <button 
                          onClick={() => handleRemovePlayer(player.user_id)}
                          className="p-1.5 rounded-md text-[#ff4d4f] hover:bg-red-50 transition-colors cursor-pointer"
                          title="Remove Player"
                        >
                          <span className="text-lg leading-none">&times;</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))
             )}
           </div>
         </div>

          {/* RSVP for current player */}
           {/* Removed redundant RSVP footer as per high-fidelity design requirements */}
          </div>
  
       {/* Floating Bottom Bar */}
        <div className="fixed bottom-28 left-1/2 -translate-x-1/2 w-full max-w-[400px] px-4 z-10 flex flex-col items-center gap-4">
          <div className="inline-flex items-center gap-1.5 bg-white text-[#006855] rounded-full px-3.5 py-1.5 shadow-sm text-xs font-bold border border-[#eee]">
            <div className="w-1.5 h-1.5 rounded-full bg-[#006855] animate-pulse" />
            {canStart ? 'Ready to start!' : 'Need 2 confirmed players to start'}
          </div>
            <button
              onClick={handleStart}
              disabled={isReadOnly || !canStart || starting || !isAdmin}
               className={`relative w-full bg-[#006855] text-white border-none rounded-full py-4 px-6 cursor-pointer flex items-center justify-between shadow-lg transition-all active:scale-95 ${(!isReadOnly && canStart && !starting && isAdmin) ? 'opacity-100' : 'opacity-50'}`}
            >
            <div className="text-left">
              <div className="text-[9px] font-black uppercase tracking-widest opacity-80 leading-tight">READY TO PLAY?</div>
              <div className="text-lg font-black leading-tight">{starting ? 'Starting...' : 'Start Game'}</div>
            </div>
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
            </div>
          </button>
        </div>

 
      {/* Add Player Modal */}
      <AddPlayerModal
        isOpen={showAddPlayerModal}
        onClose={() => setShowAddPlayerModal(false)}
        onAddPlayer={handleAddPlayer}
        availableUsers={users.filter(u => !game.players.some(p => p.user_id === u.id))}
      />

      {/* Google Login FAB */}
      {!firebaseUser && <GoogleLoginFAB onSignIn={signIn} />}
    </div>
  );
}
